import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Mail, Phone } from 'lucide-react';

const PrakritiResults = ({ scores, userInfo }) => {
  const [percentages, setPercentages] = useState({ vata: 0, pitta: 0, kapha: 0 });
  const [dominantDosha, setDominantDosha] = useState('');

  useEffect(() => {
    const total = scores.vata + scores.pitta + scores.kapha;
    const calculated = {
      vata: ((scores.vata / total) * 100).toFixed(1),
      pitta: ((scores.pitta / total) * 100).toFixed(1),
      kapha: ((scores.kapha / total) * 100).toFixed(1)
    };
    setPercentages(calculated);

    // Determine dominant dosha
    if (scores.vata > scores.pitta && scores.vata > scores.kapha) {
      setDominantDosha('Vata');
    } else if (scores.pitta > scores.vata && scores.pitta > scores.kapha) {
      setDominantDosha('Pitta');
    } else if (scores.kapha > scores.vata && scores.kapha > scores.pitta) {
      setDominantDosha('Kapha');
    } else {
      setDominantDosha('Balanced');
    }
  }, [scores]);

  const doshaInfo = {
    Vata: {
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      description: 'Vata types are creative, energetic, and quick-thinking. They thrive on variety and movement.',
      characteristics: ['Creative', 'Energetic', 'Quick-minded', 'Flexible'],
      recommendations: ['Maintain regular routines', 'Eat warm, cooked foods', 'Practice calming yoga', 'Get adequate rest']
    },
    Pitta: {
      color: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700',
      description: 'Pitta types are intense, focused, and goal-oriented. They have strong digestion and leadership qualities.',
      characteristics: ['Intelligent', 'Focused', 'Strong-willed', 'Organized'],
      recommendations: ['Stay cool and calm', 'Eat cooling foods', 'Avoid excessive heat', 'Practice moderation']
    },
    Kapha: {
      color: 'from-green-500 to-teal-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      description: 'Kapha types are calm, grounded, and nurturing. They have excellent stamina and stability.',
      characteristics: ['Calm', 'Stable', 'Nurturing', 'Patient'],
      recommendations: ['Stay active', 'Eat light, warm foods', 'Exercise regularly', 'Seek stimulation']
    }
  };

  const info = doshaInfo[dominantDosha] || doshaInfo.Vata;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container mx-auto px-4 py-12"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.h1
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="text-5xl font-bold text-gray-800 mb-4"
          >
            Your Prakriti Analysis
          </motion.h1>
          <p className="text-xl text-gray-600">
            Hello {userInfo.name}, here are your results!
          </p>
        </div>

        {/* Dosha Visualization - Interactive Bars */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            Your Dosha Distribution
          </h2>

          <div className="space-y-8">
            {/* Vata */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-xl font-bold text-purple-700">Vata</span>
                <span className="text-2xl font-bold text-purple-700">{percentages.vata}%</span>
              </div>
              <div className="h-12 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percentages.vata}%` }}
                  transition={{ duration: 1, delay: 0.2 }}
                  className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-end pr-4"
                >
                  <span className="text-white font-bold text-sm">
                    {percentages.vata > 10 && `${percentages.vata}%`}
                  </span>
                </motion.div>
              </div>
            </div>

            {/* Pitta */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-xl font-bold text-orange-700">Pitta</span>
                <span className="text-2xl font-bold text-orange-700">{percentages.pitta}%</span>
              </div>
              <div className="h-12 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percentages.pitta}%` }}
                  transition={{ duration: 1, delay: 0.4 }}
                  className="h-full bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-end pr-4"
                >
                  <span className="text-white font-bold text-sm">
                    {percentages.pitta > 10 && `${percentages.pitta}%`}
                  </span>
                </motion.div>
              </div>
            </div>

            {/* Kapha */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-xl font-bold text-green-700">Kapha</span>
                <span className="text-2xl font-bold text-green-700">{percentages.kapha}%</span>
              </div>
              <div className="h-12 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percentages.kapha}%` }}
                  transition={{ duration: 1, delay: 0.6 }}
                  className="h-full bg-gradient-to-r from-green-500 to-teal-600 rounded-full flex items-center justify-end pr-4"
                >
                  <span className="text-white font-bold text-sm">
                    {percentages.kapha > 10 && `${percentages.kapha}%`}
                  </span>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Dominant Dosha Info */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1 }}
          className={`${info.bgColor} rounded-3xl p-8 md:p-12 mb-8`}
        >
          <div className="text-center mb-6">
            <h2 className={`text-4xl font-bold ${info.textColor} mb-4`}>
              Your Dominant Dosha: {dominantDosha}
            </h2>
            <p className="text-lg text-gray-700">{info.description}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-8">
            {/* Characteristics */}
            <div>
              <h3 className={`text-2xl font-bold ${info.textColor} mb-4`}>
                Key Characteristics
              </h3>
              <ul className="space-y-2">
                {info.characteristics.map((char, idx) => (
                  <li key={idx} className="flex items-center space-x-2">
                    <span className={`w-2 h-2 rounded-full ${info.textColor.replace('text-', 'bg-')}`}></span>
                    <span className="text-gray-700">{char}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Recommendations */}
            <div>
              <h3 className={`text-2xl font-bold ${info.textColor} mb-4`}>
                Lifestyle Recommendations
              </h3>
              <ul className="space-y-2">
                {info.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-center space-x-2">
                    <span className={`w-2 h-2 rounded-full ${info.textColor.replace('text-', 'bg-')}`}></span>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Fertility-Specific Insights */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="bg-gradient-to-r from-primary-teal to-teal-700 rounded-3xl p-8 md:p-12 text-white mb-8"
        >
          <h2 className="text-3xl font-bold mb-6">Fertility Insights for {dominantDosha} Prakriti</h2>
          <p className="text-lg mb-6">
            Understanding your Prakriti is crucial for personalized fertility treatment. 
            Dr. Anupama will use this analysis to create a customized treatment plan specifically for your body type.
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
            <p className="text-lg font-semibold mb-2">✨ What's Next?</p>
            <ul className="space-y-2">
              <li>• A detailed PDF report has been sent to your email</li>
              <li>• Our team will review your results and contact you within 24 hours</li>
              <li>• Book a consultation to discuss your personalized fertility plan</li>
            </ul>
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <div className="grid md:grid-cols-3 gap-6">
          <motion.a
            href="/contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white p-6 rounded-2xl shadow-lg text-center hover:shadow-xl transition-all"
          >
            <Phone className="w-10 h-10 text-primary-teal mx-auto mb-3" />
            <h3 className="font-bold text-gray-800 mb-2">Book Consultation</h3>
            <p className="text-sm text-gray-600">Schedule your appointment</p>
          </motion.a>

          <motion.a
            href="mailto:info@fertilityayurveda.com"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white p-6 rounded-2xl shadow-lg text-center hover:shadow-xl transition-all"
          >
            <Mail className="w-10 h-10 text-primary-teal mx-auto mb-3" />
            <h3 className="font-bold text-gray-800 mb-2">Email Results</h3>
            <p className="text-sm text-gray-600">Get full report via email</p>
          </motion.a>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.print()}
            className="bg-white p-6 rounded-2xl shadow-lg text-center hover:shadow-xl transition-all"
          >
            <Download className="w-10 h-10 text-primary-teal mx-auto mb-3" />
            <h3 className="font-bold text-gray-800 mb-2">Download PDF</h3>
            <p className="text-sm text-gray-600">Save your results</p>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default PrakritiResults;